import {
  useIsMobile,
  WSField,
  WSFlexBox,
  WSForm,
  WSInputMask,
  WSInputText,
  WSList,
  WSSelect
} from "@wingspanhq/fe-component-library";
import { IAddress, IMember } from "@wingspanhq/users/dist/lib";
import * as Yup from "yup";
import { US_STATES_OPTIONS } from "../../../constants/user";
import { SELECT_OPTIONS_CANADIAN_PROVINCES } from "../../../shared/constants/user";
import {
  validatorAddress,
  validatorAddressIncludePOBox
} from "../../../shared/validators/validatorAddress";
import { validatorStringTextOnly } from "../../../shared/validators/validatorStringTextOnly";

export const getValidationSchemaAddress = (
  country: string,
  options?: {
    allowPOBox?: boolean;
  }
) => {
  const validatorAddressResolved = options?.allowPOBox
    ? validatorAddressIncludePOBox
    : validatorAddress;

  return Yup.object().shape({
    addressLine1: validatorAddressResolved.required("Required"),
    addressLine2: validatorAddressResolved,
    city: validatorStringTextOnly.required("Required"),
    state:
      country === "US" || country === "CA"
        ? Yup.string()
            .test("valid-state", "Not valid", value => {
              if (!value) {
                return false;
              }

              switch (country) {
                case "US":
                  return US_STATES_OPTIONS.some(state => state.value === value);
                case "CA":
                  return SELECT_OPTIONS_CANADIAN_PROVINCES.some(
                    province => province.value === value
                  );
                default:
                  return false;
              }
            })
            .required("Required")
        : Yup.string()
            .nullable()
            .test(
              "no-invalid-chars",
              "Cannot contain parentheses () or underscores _",
              value => {
                if (!value) {
                  return true; // null/empty is allowed
                }
                return !/[()_]/.test(value);
              }
            ),
    postalCode: Yup.string()
      .required("Required")
      .test("valid-postal-code", "Not valid", value => {
        if (!value) {
          return false;
        }

        switch (country) {
          case "US":
            return /^\d{5}$/.test(value);
          case "CA":
            return /^[ABCEGHJ-NPRSTVXY][0-9][ABCEGHJ-NPRSTV-Z][ ]?[0-9][ABCEGHJ-NPRSTV-Z][0-9]$/.test(
              value
            );
          default:
            return /^[a-zA-Z0-9 -]+$/.test(value);
        }
      })
  });
};

export type FormDataAddress = {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string | null;
  postalCode: string;
};

export function formDataToProfile(
  country: string,
  data: FormDataAddress
): IAddress {
  return {
    country,
    addressLine1: data.addressLine1,
    addressLine2: data.addressLine2,
    city: data.city,
    state: data.state || undefined,
    postalCode: data.postalCode
  };
}

export function getProfileDataAddress(
  member?: IMember,
  options?: {
    homeAddress?: boolean;
  }
): Partial<FormDataAddress> {
  const profileAddress = options?.homeAddress
    ? member?.profile.homeAddress
    : member?.profile.address;

  return {
    addressLine1: profileAddress?.addressLine1,
    addressLine2: profileAddress?.addressLine2,
    city: profileAddress?.city,
    state: profileAddress?.state,
    postalCode: profileAddress?.postalCode
  };
}

type Props = {
  country: string;
  name: string;
  label?: string;
  showRequirements?: boolean;
};

export const FormPartialAddress: React.FC<Props> = ({
  country,
  label,
  name,
  showRequirements = true
}) => {
  const isMobile = useIsMobile();
  return (
    <WSField as="div" label={label} required>
      <WSList gap="M">
        <WSForm.Field
          label="Street address"
          name={name + ".addressLine1"}
          component={WSInputText}
          componentProps={{
            placeholder: "Street address",
            fsExclude: true,
            required: showRequirements,
            autoComplete: "address-line1"
          }}
        />
        <WSForm.Field
          label={`Street address line 2 ${
            showRequirements ? "(optional)" : ""
          }`}
          name={name + ".addressLine2"}
          component={WSInputText}
          componentProps={{
            placeholder: "Street address line 2",
            fsExclude: showRequirements,
            autoComplete: "address-line2"
          }}
        />

        {country === "US" ? (
          <WSFlexBox
            wrap="nowrap"
            gap="M"
            key={country + "-city-state-postal"}
            direction={isMobile ? "column" : "row"}
          >
            <WSForm.Field
              label="City"
              name={name + ".city"}
              component={WSInputText}
              componentProps={{
                placeholder: "City",
                required: showRequirements,
                autoComplete: "address-level2"
              }}
            />

            <WSForm.Field
              label="State"
              name={name + ".state"}
              component={WSSelect}
              componentProps={{
                placeholder: "State",
                internalSearch: true,
                options: US_STATES_OPTIONS,
                required: showRequirements
              }}
            />

            <WSForm.Field
              label="Zip code"
              name={name + ".postalCode"}
              component={WSInputMask}
              componentProps={{
                placeholder: "Zip code",
                mask: "99999",
                required: showRequirements,
                autoComplete: "postal-code"
              }}
            />
          </WSFlexBox>
        ) : country === "CA" ? (
          <WSFlexBox
            wrap="nowrap"
            gap="M"
            key={country + "-city-state-postal"}
            direction={isMobile ? "column" : "row"}
          >
            <WSForm.Field
              label="City"
              name={name + ".city"}
              component={WSInputText}
              componentProps={{
                placeholder: "City",
                required: showRequirements,
                autoComplete: "address-level2"
              }}
            />

            <WSForm.Field
              label="Province"
              name={name + ".state"}
              component={WSSelect}
              componentProps={{
                placeholder: "Province",
                internalSearch: true,
                options: SELECT_OPTIONS_CANADIAN_PROVINCES,
                required: showRequirements
              }}
            />

            <WSForm.Field
              label="Postal code"
              name={name + ".postalCode"}
              component={WSInputText}
              componentProps={{
                placeholder: "Postal code",
                required: showRequirements,
                autoComplete: "postal-code"
              }}
            />
          </WSFlexBox>
        ) : (
          <WSFlexBox
            wrap="nowrap"
            gap="M"
            key={country + "-city-state-postal"}
            direction={isMobile ? "column" : "row"}
          >
            <WSForm.Field
              label="City"
              name={name + ".city"}
              component={WSInputText}
              componentProps={{
                placeholder: "City / Town",
                required: showRequirements,
                autoComplete: "address-level2"
              }}
            />

            <WSForm.Field
              label="Region/state (optional)"
              name={name + ".state"}
              component={WSInputText}
              componentProps={{
                placeholder: "Region/state (optional)",
                autoComplete: "address-level1"
              }}
            />

            <WSForm.Field
              label="Postal code"
              name={name + ".postalCode"}
              component={WSInputText}
              componentProps={{
                placeholder: "Postal code",
                required: showRequirements,
                autoComplete: "postal-code"
              }}
            />
          </WSFlexBox>
        )}
      </WSList>
    </WSField>
  );
};
